import RIGHTS_KEYS from '../../constants/rights-keys';
import PERMISSION_KEYS from '../../constants/permission-keys';

export interface AccessRow {
    key: string;
    label: string;
    type: 'Right' | 'Permission';
    access: 'read' | 'write';
    description: string;
}

export interface SubGroup {
    label: string;
    rows: AccessRow[];
}

export interface TopGroup {
    group: string;
    subgroups: SubGroup[];
}

export const RIGHTS_GROUPS: TopGroup[] = [
    {
        group: 'Functional',
        subgroups: [
            {
                label: 'Document Schemas',
                rows: [
                    { key: RIGHTS_KEYS.manageDocumentSchemas, label: 'ManageDocumentSchemas', type: 'Right', access: 'write', description: 'Manage document schemas' },
                    { key: RIGHTS_KEYS.viewDocumentSchemas, label: 'ViewDocumentSchemas', type: 'Right', access: 'read', description: 'View document schemas' },
                ],
            },
        ],
    },
    {
        group: 'Backend Admin',
        subgroups: [
            {
                label: 'Users & Orgs',
                rows: [
                    { key: RIGHTS_KEYS.manageActors, label: 'ManageActors', type: 'Right', access: 'write', description: 'Create/modify actor roles' },
                    { key: RIGHTS_KEYS.manageEndUsers, label: 'ManageEndUsers', type: 'Right', access: 'write', description: 'View department-scoped actors and their users' },
                    { key: RIGHTS_KEYS.manageAllUsers, label: 'ManageAllUsers', type: 'Right', access: 'write', description: 'Manage all users globally' },
                    { key: RIGHTS_KEYS.manageLicenses, label: 'ManageLicenses', type: 'Right', access: 'write', description: 'Manage licenses' },
                    { key: RIGHTS_KEYS.manageTrialUsers, label: 'ManageTrialUsers', type: 'Right', access: 'write', description: 'Manage trial users' },
                    { key: RIGHTS_KEYS.manageDepartments, label: 'ManageDepartments', type: 'Right', access: 'write', description: 'Create/manage departments' },
                ],
            },
            {
                label: 'Implants',
                rows: [
                    { key: RIGHTS_KEYS.addImplant, label: 'AddImplant', type: 'Right', access: 'write', description: 'Add implants to the database' },
                    { key: RIGHTS_KEYS.manageAllImplants, label: 'ManageAllImplants', type: 'Right', access: 'write', description: 'Manage all implants globally' },
                    { key: RIGHTS_KEYS.manageImplant, label: 'ManageManufacturerImplants', type: 'Right', access: 'write', description: 'Manage manufacturer implants' },
                    { key: RIGHTS_KEYS.reverifyImplants, label: 'ReverifyImplants', type: 'Right', access: 'write', description: 'Re-verify implants' },
                    { key: RIGHTS_KEYS.verifyImplants, label: 'VerifyImplants', type: 'Right', access: 'write', description: 'Verify implants' },
                    { key: RIGHTS_KEYS.managePredefined, label: 'ManagePredefinedConditionals', type: 'Right', access: 'write', description: 'Manage predefined conditionals' },
                ],
            },
            {
                label: 'Content & Comms',
                rows: [
                    { key: RIGHTS_KEYS.createNews, label: 'CreateNews', type: 'Right', access: 'write', description: 'Create global news' },
                    { key: RIGHTS_KEYS.manageEmails, label: 'ManageEmails', type: 'Right', access: 'write', description: 'Manage emails' },
                    { key: RIGHTS_KEYS.manageNotifications, label: 'ManageNotifications', type: 'Right', access: 'write', description: 'Manage notifications' },
                    { key: RIGHTS_KEYS.manageVideoUpload, label: 'ManageVideoUpload', type: 'Right', access: 'write', description: 'Manage video uploads' },
                ],
            },
        ],
    },
    {
        group: 'End-User',
        subgroups: [
            {
                label: 'Search',
                rows: [
                    { key: RIGHTS_KEYS.advancedSearch, label: 'AdvancedSearch', type: 'Right', access: 'read', description: 'Use advanced search filters/options' },
                    { key: RIGHTS_KEYS.dbSearch, label: 'DBSearch', type: 'Right', access: 'read', description: 'Search implant/device database' },
                ],
            },
            {
                label: 'Record Access',
                rows: [
                    { key: RIGHTS_KEYS.detailedInfo, label: 'DetailedInfo', type: 'Right', access: 'read', description: 'View detailed record info' },
                    { key: RIGHTS_KEYS.viewSource, label: 'ViewSource', type: 'Right', access: 'read', description: 'View source/reference details (IFU)' },
                    { key: RIGHTS_KEYS.internalInfo, label: 'Internal Info', type: 'Right', access: 'read', description: 'View internal information' },
                ],
            },
            {
                label: 'General Access',
                rows: [
                    { key: RIGHTS_KEYS.guestAccess, label: 'GuestAccess', type: 'Right', access: 'read', description: 'Access guest-level capabilities' },
                ],
            },
        ],
    },
    {
        group: 'Department',
        subgroups: [
            {
                label: 'Department Management',
                rows: [
                    { key: RIGHTS_KEYS.manageEmployees, label: 'ManageEmployees', type: 'Right', access: 'write', description: 'Manage department employees/users' },
                    { key: RIGHTS_KEYS.manageDepartmentInfo, label: 'ManageDepartmentInfo', type: 'Right', access: 'write', description: 'Edit department name and address' },
                ],
            },
            {
                label: 'MRI',
                rows: [
                    { key: RIGHTS_KEYS.manageMRIOverview, label: 'ManageMRIOverview', type: 'Right', access: 'write', description: 'Edit/manage MRI overview' },
                    { key: RIGHTS_KEYS.viewMRIOverview, label: 'ViewMRIOverview', type: 'Right', access: 'read', description: 'View MRI overview' },
                ],
            },
            {
                label: 'Policies & Documents',
                rows: [
                    { key: RIGHTS_KEYS.viewPolicies, label: 'ViewPolicies', type: 'Right', access: 'read', description: 'View policy documents' },
                    { key: RIGHTS_KEYS.viewDepartmentFiles, label: 'ViewDepartmentFiles', type: 'Right', access: 'read', description: 'View department files' },
                ],
            },
            {
                label: 'Tools & Reporting',
                rows: [
                    { key: RIGHTS_KEYS.deptTools, label: 'DepartmentTools', type: 'Right', access: 'read', description: 'Access department tools' },
                    { key: RIGHTS_KEYS.cptReporting, label: 'CPTReporting', type: 'Right', access: 'read', description: 'Access CPT reporting' },
                    { key: RIGHTS_KEYS.createDeptNews, label: 'CreateDepartmentNews', type: 'Right', access: 'write', description: 'Create department news posts' },
                ],
            },
        ],
    },
];

export const PERMISSION_GROUP: TopGroup = {
    group: 'Department Permissions Template',
    subgroups: [
        {
            label: 'Permissions',
            rows: [
                { key: PERMISSION_KEYS.approve, label: 'Approve', type: 'Permission', access: 'write', description: 'Approve workflow items' },
                { key: PERMISSION_KEYS.approvePolicy, label: 'Approve Policy', type: 'Permission', access: 'write', description: 'Approve policy documents' },
                { key: PERMISSION_KEYS.cptAnalytics, label: 'CPT Analytics', type: 'Permission', access: 'read', description: 'Access CPT analytics/reporting' },
                { key: PERMISSION_KEYS.manageFiles, label: 'Manage Files', type: 'Permission', access: 'write', description: 'Manage files/documents' },
                { key: PERMISSION_KEYS.manageGroups, label: 'Manage Groups', type: 'Permission', access: 'write', description: 'Manage groups/memberships' },
                { key: PERMISSION_KEYS.manageNotes, label: 'Manage Notes', type: 'Permission', access: 'write', description: 'Manage notes/annotations' },
                { key: PERMISSION_KEYS.managePolicies, label: 'Manage Policies', type: 'Permission', access: 'write', description: 'Create/edit/manage policies' },
                { key: PERMISSION_KEYS.manageProtocols, label: 'Manage Protocols', type: 'Permission', access: 'write', description: 'Create/edit/manage protocols' },
            ],
        },
    ],
};

export const ALL_GROUPS: TopGroup[] = [...RIGHTS_GROUPS, PERMISSION_GROUP];

export const PERMISSION_ROWS: AccessRow[] = PERMISSION_GROUP.subgroups[0].rows;

export const RIGHT_ROWS: AccessRow[] = RIGHTS_GROUPS.flatMap((g) =>
    g.subgroups.flatMap((s) => s.rows)
);
